import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, ExternalLink, Loader2, MoveRight, Search, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useOrganizationProjectsQuery, useAddProjectToOrganizationMutation, useAssignableProjectsQuery } from "@/hooks/queries/useOrganizationsQuery";
import { ProjectPhase, ProjectStatus } from "@/types/project";
import type { Project } from "@/types/project";

const PAGE_SIZE = 20;

interface OrganizationProjectsTabProps {
  organizationId: number;
  canMoveProjects: boolean;
}

const OrganizationProjectsTab = ({ organizationId, canMoveProjects }: OrganizationProjectsTabProps) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [page, setPage] = useState(1);
  const { data: projectsData, isLoading } = useOrganizationProjectsQuery(organizationId, {
    page,
    pageSize: PAGE_SIZE,
    sortBy: "name",
    sortDirection: "asc",
  });
  const addProjectMutation = useAddProjectToOrganizationMutation();
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const { data: assignableProjects = [], isLoading: isSearchLoading, isFetching: isSearchFetching } =
    useAssignableProjectsQuery(organizationId, search, showMoveDialog);

  const organizationProjects = projectsData?.items ?? [];
  const totalCount = projectsData?.totalCount ?? 0;
  const totalPages = projectsData?.totalPages ?? 1;

  useEffect(() => {
    if (!showMoveDialog) {
      setSearch("");
      setSelectedProject(null);
    }
  }, [showMoveDialog]);

  const getStatusLabel = (status: ProjectStatus | null) => {
    switch (status) {
      case ProjectStatus.Active: return t("active");
      case ProjectStatus.OnHold: return t("onHold");
      case ProjectStatus.Cancelled: return t("cancelled");
      case ProjectStatus.Done: return t("done");
      default: return "-";
    }
  };

  const getPhaseLabel = (phase: ProjectPhase | null) => {
    switch (phase) {
      case ProjectPhase.InitialReview: return t("initialReview");
      case ProjectPhase.DueDiligence: return t("dueDiligence");
      case ProjectPhase.Negotiation: return t("negotiation");
      case ProjectPhase.Closing: return t("closing");
      default: return "-";
    }
  };

  const handleMoveProject = () => {
    if (!selectedProject) return;
    addProjectMutation.mutate(
      { organizationId, projectId: selectedProject.id },
      { onSuccess: () => setShowMoveDialog(false) }
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FolderKanban className="h-5 w-5" />
          {t("totalProjects")}: {totalCount}
        </div>
        {canMoveProjects && (
          <Button size="sm" className="w-full sm:w-auto" onClick={() => setShowMoveDialog(true)}>
            <MoveRight className="mr-2 h-4 w-4" />
            {t("addExistingProject")}
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("projectTarget")}</TableHead>
                <TableHead>{t("status")}</TableHead>
                <TableHead>{t("phase")}</TableHead>
                <TableHead>{t("owner")}</TableHead>
                <TableHead className="w-24">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizationProjects.map((project) => (
                <TableRow key={project.id}>
                  <TableCell className="font-medium">{project.name}</TableCell>
                  <TableCell>{getStatusLabel(project.status)}</TableCell>
                  <TableCell>{getPhaseLabel(project.phase)}</TableCell>
                  <TableCell>{project.owner || "-"}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/project/${project.id}`)} aria-label={t("openProject")}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {organizationProjects.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">{t("noProjectsFound")}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={page <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            <PaginationItem>
              <span className="px-4 text-sm text-muted-foreground">
                {page} / {totalPages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className={page >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("addExistingProject")}</DialogTitle>
            <DialogDescription>{t("moveProjectWarning")}</DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={t("searchProjectsPlaceholder")}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedProject(null);
              }}
              autoFocus
            />
          </div>

          <ScrollArea className="h-64 rounded-md border">
            {isSearchLoading || isSearchFetching ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : assignableProjects.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {search.trim() ? t("noMatchingProjects") : t("searchToFindProjects")}
              </p>
            ) : (
              <div className="p-1" role="listbox" aria-label={t("searchProjectsPlaceholder")}>
                {assignableProjects.map((project) => {
                  const isSelected = selectedProject?.id === project.id;
                  return (
                    <button
                      key={project.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => setSelectedProject(isSelected ? null : project)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent",
                        isSelected && "bg-accent"
                      )}
                    >
                      <span className="flex flex-col items-start gap-0.5">
                        <span className="font-medium">{project.name}</span>
                        <span className="text-xs text-muted-foreground">{project.organizationName}</span>
                      </span>
                      {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>{t("cancel")}</Button>
            <Button onClick={handleMoveProject} disabled={!selectedProject || addProjectMutation.isPending}>
              {addProjectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("moveProject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationProjectsTab;
